import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getActiveTenantName } from "../global/tenant";
import store from "../store/store";

// One shared STOMP client for the whole app, replacing the previous pattern
// where each *WebSocketService.js opened its own `new Client(...)`. The point
// is api-gw's HttpClient pool: Spring Cloud Netflix Zuul 1.x defaults
// `max-per-route-connections` to 20, and a single browser tab used to chew
// 8 of those slots (one per service) before any REST traffic. Sharing collapses
// that to 1 — see the postmortem in PR #N for the full chain.

let _client = null;
let _connected = false;
let _idCounter = 0;
const _subscriptions = new Map();

// Resolvers waiting for the next successful connect. ensureConnected() parks
// callers here so a subscribe-then-POST flow can guarantee its STOMP SUBSCRIBE
// frame is live on the server before the REST call triggers the producer —
// otherwise early frames (STARTED/QUEUED) emitted before the subscription lands
// are lost (the simple broker has no replay).
let _connectWaiters = [];

// Reads JWT + tenant from the session and returns the STOMP connect headers.
// Exported so legacy components that still own their own Client can reuse the
// same logic instead of duplicating the localStorage reads.
export function buildConnectHeaders() {
  const token = localStorage.getItem("jwtToken");
  const tenantName = getActiveTenantName(store.getState());
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantName) headers["X-tenant"] = tenantName;
  return headers;
}

const getWsBase = () => {
  if (window.location.hostname === "localhost") {
    return "http://localhost:8082/ws";
  }
  return `${window.location.origin}/api/pda/ws`;
};

function ensureClient() {
  if (_client) return;
  _client = new Client({
    webSocketFactory: () => new SockJS(getWsBase()),
    // Re-read the JWT before every (re)connect — a token rotation during the
    // session would otherwise trap us with a stale Authorization header on
    // the next reconnect attempt.
    beforeConnect: () => {
      _client.connectHeaders = buildConnectHeaders();
    },
    // Negotiated against the server's broker heartbeat (25s each way). Keeps the
    // socket alive through long silent server-side processing so proxies don't
    // reap it mid-import.
    heartbeatIncoming: 25000,
    heartbeatOutgoing: 25000,
    reconnectDelay: 5000,
    onConnect: () => {
      _connected = true;
      // (Re)subscribe everything on every successful connect — same path
      // handles initial connect and reconnect after a transient blip, since
      // the server forgets STOMP subscriptions across socket reopens.
      _subscriptions.forEach((entry) => {
        entry.stompSub = _client.subscribe(entry.topic, entry.callback);
      });
      // Release ensureConnected() waiters only after the resubscribe loop, so
      // their subscriptions are guaranteed live before the caller proceeds.
      const waiters = _connectWaiters;
      _connectWaiters = [];
      waiters.forEach((resolve) => resolve());
    },
    onWebSocketClose: () => {
      _connected = false;
      // STOMP sub handles point at a dead socket now; null them so the next
      // onConnect re-creates instead of trying to reuse them.
      _subscriptions.forEach((entry) => {
        entry.stompSub = null;
      });
    },
    // Intentionally silent at the singleton level. Per-service UX (toast,
    // retry, abort) lives in the caller — the singleton just maintains the
    // pipe.
    onStompError: () => {},
    onWebSocketError: () => {},
  });
  _client.activate();
}

// How long a caller will wait for the socket before giving up. Without this,
// a dropped socket that can't re-establish (connection storm during a refetch,
// proxy reaping the WS) parks the caller's waiter forever — so the next async
// job (import, delete, copy…) never fires its POST and hangs silently with no
// error card. Rejecting lets the caller surface a retryable error instead.
const CONNECT_TIMEOUT_MS = 20000;

/**
 * Resolves once the shared client is connected (and any pending subscriptions
 * have been (re)applied in onConnect). Lazy-activates the client on first call.
 *
 * Callers that subscribe then trigger a server-side producer over REST should
 * `await ensureConnected()` between the two: subscribeStomp() registers the
 * subscription synchronously, and this resolves only after onConnect has
 * applied it — closing the race where the producer emits before the SUBSCRIBE.
 *
 * Rejects with an Error after `timeoutMs` if the socket never (re)connects, so
 * callers don't hang forever on a dead pipe.
 */
export function ensureConnected(timeoutMs = CONNECT_TIMEOUT_MS) {
  ensureClient();
  if (_connected) return Promise.resolve();

  // The client auto-reconnects via reconnectDelay, but if it was explicitly
  // deactivated (disconnectStomp on logout) or its activation lapsed, it won't
  // come back on its own — kick it so the waiter below can resolve.
  if (_client && _client.active === false) {
    _client.activate();
  }

  return new Promise((resolve, reject) => {
    const waiter = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = setTimeout(() => {
      _connectWaiters = _connectWaiters.filter((w) => w !== waiter);
      reject(new Error("STOMP connection timed out"));
    }, timeoutMs);
    _connectWaiters.push(waiter);
  });
}

/**
 * Subscribe to a STOMP topic over the shared connection. The first call
 * lazy-activates the underlying SockJS client; subsequent calls reuse it.
 *
 * Returns an idempotent unsubscribe function. Safe to call multiple times.
 */
export function subscribeStomp(topic, callback) {
  ensureClient();
  const id = ++_idCounter;
  const entry = { topic, callback, stompSub: null };
  _subscriptions.set(id, entry);
  if (_connected) {
    entry.stompSub = _client.subscribe(topic, callback);
  }
  return function unsubscribe() {
    const e = _subscriptions.get(id);
    if (!e) return;
    if (e.stompSub) {
      try {
        e.stompSub.unsubscribe();
      } catch {
        // ignore — socket may already be dead
      }
    }
    _subscriptions.delete(id);
  };
}

/**
 * Tear down the shared client and forget all subscriptions. Intended for
 * logout / hard auth-state transitions. New subscribeStomp() calls after
 * this will lazy-create a fresh client.
 */
export function disconnectStomp() {
  if (_client) {
    try {
      _client.deactivate();
    } catch {
      // ignore
    }
    _client = null;
  }
  _connected = false;
  _subscriptions.clear();
  _connectWaiters = [];
}
